"use client";

import { useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Fade,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { CheckCircle, Download, Link as LinkIcon } from "@mui/icons-material";

function getFilenameFromContentDisposition(
  contentDisposition?: string
): string | null {
  if (!contentDisposition) return null;
  const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(
    contentDisposition
  );
  const raw = match?.[1] || match?.[2];
  return raw ? decodeURIComponent(raw) : null;
}

function isXUrl(value: string): boolean {
  try {
    const parsed = new URL(value);
    const host = parsed.hostname.toLowerCase();
    return (
      host === "x.com" ||
      host === "www.x.com" ||
      host === "twitter.com" ||
      host === "www.twitter.com" ||
      host === "mobile.twitter.com" ||
      host === "m.twitter.com"
    );
  } catch {
    return false;
  }
}

export default function XVideoDownloader() {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleDownload = async () => {
    const value = url.trim();

    if (!value) {
      setError("Please paste an x.com post URL.");
      return;
    }

    if (!isXUrl(value)) {
      setError("Please enter a valid x.com or twitter.com URL.");
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await axios.post(
        "/api/x-video-downloader",
        { url: value },
        {
          responseType: "blob",
          timeout: 600000,
        }
      );

      const blob = new Blob([response.data], { type: "video/mp4" });
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download =
        getFilenameFromContentDisposition(
          response.headers["content-disposition"]
        ) || "x-video.mp4";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);

      setSuccess(true);
    } catch (err) {
      let message =
        "Failed to download video from X. Please try another post URL.";

      if (axios.isAxiosError(err)) {
        if (err.response?.data instanceof Blob) {
          try {
            const text = await err.response.data.text();
            const parsed = JSON.parse(text) as { error?: string };
            if (parsed.error) {
              message = parsed.error;
            }
          } catch {
            message = err.message || message;
          }
        } else if (typeof err.response?.data?.error === "string") {
          message = err.response.data.error;
        } else if (err.message) {
          message = err.message;
        }
      } else if (err instanceof Error) {
        message = err.message;
      }

      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 900, mx: "auto" }}>
      <Paper
        elevation={8}
        sx={{
          p: 4,
          borderRadius: 4,
          background: "rgba(255, 255, 255, 0.98)",
          backdropFilter: "blur(10px)",
        }}
      >
        <Stack spacing={2}>
          <Typography variant="h6" component="p" fontWeight={600}>
            Paste X Post URL
          </Typography>

          <TextField
            fullWidth
            placeholder="https://x.com/username/status/1234567890"
            value={url}
            onChange={(e) => {
              setUrl(e.target.value);
              setError(null);
              setSuccess(false);
            }}
            disabled={loading}
            error={!!error}
            InputProps={{
              startAdornment: (
                <LinkIcon sx={{ color: "text.secondary", mr: 1 }} />
              ),
            }}
          />

          <Button
            variant="contained"
            size="large"
            onClick={handleDownload}
            disabled={loading || !url.trim()}
            startIcon={<Download sx={{ color: "#ffffff" }} />}
            sx={{
              py: 1.5,
              fontSize: "1.05rem",
              fontWeight: 700,
              color: "#ffffff",
              backgroundColor: "#5558d3",
              "&:hover": {
                backgroundColor: "#4448c2",
              },
              "&:disabled": {
                backgroundColor: "grey.300",
              },
            }}
          >
            {loading ? "Downloading..." : "Download Video"}
          </Button>

          {loading && (
            <Box>
              <LinearProgress
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: "grey.200",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 3,
                    background:
                      "linear-gradient(45deg, #667eea 30%, #764ba2 90%)",
                  },
                }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                align="center"
                sx={{ mt: 1 }}
              >
                Fetching and preparing the video. This can take up to a minute.
              </Typography>
            </Box>
          )}

          {error && !loading && (
            <Fade in={!!error}>
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            </Fade>
          )}

          {success && !loading && (
            <Fade in={success}>
              <Alert
                icon={<CheckCircle fontSize="inherit" />}
                severity="success"
                onClose={() => setSuccess(false)}
              >
                Video downloaded successfully.
              </Alert>
            </Fade>
          )}

          <Typography variant="body2" color="text.secondary">
            Only download content you have rights to use. Private, age-gated, or
            region-restricted posts may not be available.
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
}
